import { useCallback } from 'react';
import { Translations } from '../model/translationModel';
import { namespacedKeys } from '../utils/namespacedKeys';
import { interpolate } from '../utils/interpolateText';
import { TranslationKey } from '~/lib/data-contract';
import { NodeStorageKey, SafeNodeStorage } from '~/services/storage';
import { useIsDebug } from '~/shared/hooks/useIsDebug';

// Initialize storage (Node only)
let serverStorage: SafeNodeStorage | null = null;
let clientTranslations: Translations | null = null;
let serverTranslations: Translations | null = null;
let serverTranslationsSource = '';

const emptyTranslations: Record<string, string> = {};

if (typeof window === 'undefined') {
    const { LocalStorage } = require('node-localstorage');
    serverStorage = new SafeNodeStorage(new LocalStorage('./scratch'));
}

const getClientTranslations = (translations: Translations | null) => {
    if (!clientTranslations) {
        clientTranslations = namespacedKeys(translations ?? emptyTranslations);
    }

    return clientTranslations;
};

const getServerTranslations = (translations: Translations | null) => {
    const source = JSON.stringify(translations ?? emptyTranslations);

    // Storage can refresh translations while the server process keeps running.
    // Keep this source check so the singleton cache does not serve stale translations.
    if (!serverTranslations || serverTranslationsSource !== source) {
        serverTranslations = namespacedKeys(translations ?? emptyTranslations);
        serverTranslationsSource = source;
    }

    return serverTranslations;
};

/**
 * Translation hook, will load initial translations provided by context on initial load, and fetch new values when culture changes
 * Always try to provide a value, even if it's from the previously loaded translations, but the isLoading will indicate
 * if it's serving stale data
 */
export type translationInterpolationKeys = {
    [key: string]: string;
};

export type TranslateFn = (
    key: TranslationKey,
    interpolationKeys?: translationInterpolationKeys,
) => string;

export const useTranslation = () => {
    const isDebug = useIsDebug();

    // Server-side storage
    const storedData: Translations | null = serverStorage
        ? serverStorage.get(NodeStorageKey.TRANSLATIONS)
        : null;

    // Client-side fallback
    const windowData: Translations | null =
        typeof window !== 'undefined' ? window.__translations : null;

    // We only need to evaluate the translations data once
    const translations =
        typeof window !== 'undefined'
            ? getClientTranslations(windowData)
            : getServerTranslations(storedData);

    const translate: TranslateFn = useCallback(
        (key: TranslationKey, interpolationKeys?: translationInterpolationKeys) => {
            if (isDebug) {
                return (
                    '[' +
                    (key +
                        (interpolationKeys
                            ? ' ' + Object.keys(interpolationKeys).map((x) => `{{${x}}}`)
                            : '')) +
                    ']'
                );
            }

            const translation = translations[key];

            if (!translation) {
                console.warn(`Missing translation: ${key}`);
                return '';
            }

            return interpolationKeys ? interpolate(translation, interpolationKeys) : translation;
        },
        [isDebug, translations],
    );

    return { translate };
};
